import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
  TimerReset,
  UserRound
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import { useAuthStore } from '../../store';
import { authAPI } from '../../services/api';

const asArray = (value) => (Array.isArray(value) ? value : []);

const labelize = (value = '') => String(value || 'Not set')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())
  .replace(/\bRbac\b/g, 'RBAC')
  .replace(/\bKyc\b/g, 'KYC');

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const getInitials = (user) => (
  `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.trim() ||
  user?.email?.charAt(0)?.toUpperCase() ||
  'A'
);

const getErrorMessage = (error, fallback = 'Request failed. Please try again.') => (
  error?.response?.data?.details?.[0] ||
  error?.response?.data?.error ||
  error?.message ||
  fallback
);

const passwordRules = [
  { key: 'length', label: 'At least 8 characters', test: (value) => value.length >= 8 },
  { key: 'letter', label: 'Includes a letter', test: (value) => /[A-Za-z]/.test(value) },
  { key: 'number', label: 'Includes a number', test: (value) => /\d/.test(value) },
  { key: 'different', label: 'Different from current password', test: (value, current) => Boolean(value) && value !== current }
];

function Field({ label, children, hint, error }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-800">{label}</span>
      <div className="mt-2">{children}</div>
      {error ? (
        <p className="mt-2 text-sm font-medium text-rose-600">{error}</p>
      ) : hint ? (
        <p className="mt-2 text-xs leading-5 text-slate-500">{hint}</p>
      ) : null}
    </label>
  );
}

function PasswordInput({ value, onChange, placeholder, autoComplete, visible, onToggle, disabled }) {
  return (
    <div className="relative">
      <LockKeyhole className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        className="h-11 w-full rounded-[16px] border border-slate-200 bg-white pl-10 pr-11 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:cursor-not-allowed disabled:bg-slate-50"
      />
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed"
        aria-label={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function Panel({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="rounded-[24px] border border-slate-100 bg-white shadow-sm">
      <div className="flex items-start gap-3 border-b border-slate-100 p-5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] border border-slate-100 bg-slate-50 text-slate-900">
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold tracking-tight text-slate-950">{title}</h2>
          {subtitle && <p className="mt-1 text-sm leading-5 text-slate-500">{subtitle}</p>}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function DetailItem({ icon: Icon, label, value }) {
  return (
    <div className="rounded-[16px] border border-slate-100 bg-slate-50 p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-white text-slate-600 shadow-sm border border-slate-100">
          <Icon size={16} />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
          <p className="mt-1 break-words text-sm font-semibold text-slate-950">{value || 'Not recorded'}</p>
        </div>
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="min-h-full bg-[#f8f9fa] p-4 pt-20 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-[1180px] space-y-6">
        <SkeletonBlock className="h-24 rounded-[24px]" />
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <SkeletonBlock className="h-96 rounded-[24px]" />
          <SkeletonBlock className="h-96 rounded-[24px]" />
        </div>
      </div>
    </div>
  );
}

function AdminProfilePage() {
  const navigate = useNavigate();
  const { user: storedUser, logout, updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState('account');
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [visible, setVisible] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [errors, setErrors] = useState({});

  const profileQuery = useQuery('admin-self-profile', authAPI.getProfile, {
    staleTime: 60 * 1000,
    onSuccess: (response) => {
      const nextUser = response?.user || response?.data?.user;
      if (nextUser) updateUser(nextUser);
    }
  });

  const user = profileQuery.data?.user || profileQuery.data?.data?.user || storedUser || {};
  const permissions = asArray(user.permissions);
  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'Admin user';

  const strength = useMemo(() => {
    const passed = passwordRules.filter((rule) => rule.test(form.newPassword, form.currentPassword)).length;
    if (!form.newPassword) return { count: 0, label: 'Not started', width: 0, color: 'bg-slate-200' };
    if (passed <= 1) return { count: passed, label: 'Weak', width: 25, color: 'bg-rose-500' };
    if (passed <= 3) return { count: passed, label: 'Good', width: 70, color: 'bg-amber-500' };
    return { count: passed, label: 'Strong', width: 100, color: 'bg-emerald-500' };
  }, [form.currentPassword, form.newPassword]);

  const mutation = useMutation((payload) => authAPI.changePassword(payload), {
    onSuccess: (response) => {
      toast.success(response?.message || 'Password changed. Sign in again.');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      logout({ callApi: false, reason: 'password_changed' });
      navigate('/login', { replace: true, state: { reason: 'password_changed' } });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Password change failed.'));
    }
  });

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: null, submit: null }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.currentPassword) nextErrors.currentPassword = 'Enter your current password.';
    if (form.newPassword.length < 8) nextErrors.newPassword = 'Use at least 8 characters.';
    if (form.newPassword && form.newPassword === form.currentPassword) {
      nextErrors.newPassword = 'Choose a password that is different from your current password.';
    }
    if (form.confirmPassword !== form.newPassword) nextErrors.confirmPassword = 'Passwords do not match.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    mutation.mutate(form);
  };

  if (profileQuery.isLoading && !storedUser) return <ProfileSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] p-4 pt-20 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-[1180px] space-y-6">
        <PageHeader
          eyebrow="Account"
          title="My Profile"
          description="Review your admin access and update the password used for this browser sign-in."
        />

        {profileQuery.isError && (
          <div className="rounded-[16px] border border-amber-100 bg-amber-50 p-4 text-sm font-medium text-amber-800">
            Profile details could not be refreshed. Showing the last saved session details.
          </div>
        )}

        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('account')}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition ${
              activeTab === 'account'
                ? 'border-slate-950 text-slate-950'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <UserRound size={16} />
            Account Details
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition ${
              activeTab === 'security'
                ? 'border-slate-950 text-slate-950'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <KeyRound size={16} />
            Security
          </button>
        </div>

        <div className="mx-auto max-w-3xl">
          {activeTab === 'account' && (
            <Panel
              icon={UserRound}
            title="Account Details"
            subtitle="This is the admin identity attached to your current session."
          >
            <div className="flex items-start gap-4 rounded-[16px] border border-slate-100 bg-slate-50 p-5">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[16px] bg-slate-950 text-xl font-bold text-white">
                {getInitials(user)}
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-xl font-bold tracking-tight text-slate-950">{displayName}</h2>
                <p className="mt-1 truncate text-sm font-medium text-slate-500">{user.email}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-[16px] border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                    {labelize(user.adminRoleName || user.adminRole || 'Admin')}
                  </span>
                  {user.isSuperAdmin && (
                    <span className="rounded-[16px] border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                      Super admin
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <DetailItem icon={Mail} label="Email" value={user.email} />
              <DetailItem icon={ShieldCheck} label="Role" value={labelize(user.adminRoleName || user.adminRole)} />
              <DetailItem icon={TimerReset} label="Last login" value={formatDateTime(user.lastLoginAt)} />
              <DetailItem icon={CheckCircle2} label="Last email check" value={formatDateTime(user.last2faAt)} />
            </div>

            <div className="mt-5 rounded-[16px] border border-slate-100 bg-white p-4 shadow-sm">
              <p className="text-sm font-semibold text-slate-950">Current access</p>
              <p className="mt-1 text-sm leading-5 text-slate-500">
                {user.isSuperAdmin
                  ? 'This account can access all admin areas.'
                  : `${permissions.length} permission${permissions.length === 1 ? '' : 's'} assigned to this account.`}
              </p>
              {!user.isSuperAdmin && permissions.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {permissions.slice(0, 10).map((permission) => (
                    <span key={permission} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
                      {permission}
                    </span>
                  ))}
                  {permissions.length > 10 && (
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
                      +{permissions.length - 10} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </Panel>
          )}

          {activeTab === 'security' && (
          <Panel
            icon={KeyRound}
            title="Reset Your Password"
            subtitle="Changing your password revokes existing admin sessions and requires a fresh login."
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <Field label="Current password" error={errors.currentPassword}>
                <PasswordInput
                  value={form.currentPassword}
                  onChange={(value) => setField('currentPassword', value)}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  visible={visible.currentPassword}
                  onToggle={() => setVisible((current) => ({ ...current, currentPassword: !current.currentPassword }))}
                  disabled={mutation.isLoading}
                />
              </Field>

              <Field label="New password" error={errors.newPassword} hint="Use a password that is not shared with other systems.">
                <PasswordInput
                  value={form.newPassword}
                  onChange={(value) => setField('newPassword', value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  visible={visible.newPassword}
                  onToggle={() => setVisible((current) => ({ ...current, newPassword: !current.newPassword }))}
                  disabled={mutation.isLoading}
                />
              </Field>

              <div className="rounded-[16px] border border-slate-100 bg-slate-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-950">Password strength</p>
                  <span className="text-xs font-semibold text-slate-500">{strength.label}</span>
                </div>
                <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-white">
                  <div className={`h-full rounded-full transition-all ${strength.color}`} style={{ width: `${strength.width}%` }} />
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {passwordRules.map((rule) => {
                    const passed = rule.test(form.newPassword, form.currentPassword);
                    return (
                      <span key={rule.key} className={`flex items-center gap-2 text-xs font-medium ${passed ? 'text-emerald-700' : 'text-slate-500'}`}>
                        <CheckCircle2 className={`h-3.5 w-3.5 ${passed ? 'text-emerald-500' : 'text-slate-300'}`} />
                        {rule.label}
                      </span>
                    );
                  })}
                </div>
              </div>

              <Field label="Confirm new password" error={errors.confirmPassword}>
                <PasswordInput
                  value={form.confirmPassword}
                  onChange={(value) => setField('confirmPassword', value)}
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  visible={visible.confirmPassword}
                  onToggle={() => setVisible((current) => ({ ...current, confirmPassword: !current.confirmPassword }))}
                  disabled={mutation.isLoading}
                />
              </Field>

              <div className="rounded-[16px] border border-blue-100 bg-blue-50 p-4 text-sm leading-6 text-blue-800">
                After a successful change, all admin sessions for this account are revoked and you will be sent to the sign-in page.
              </div>

              <button
                type="submit"
                disabled={mutation.isLoading}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-[16px] bg-slate-950 px-5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400 sm:w-auto"
              >
                {mutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Reset password
              </button>
            </form>
          </Panel>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminProfilePage;
